import { useState, useEffect, useCallback } from 'react';
import { Activity, Clock, AlertTriangle, BarChart3, RefreshCw, Trash2, Zap, TrendingUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function MetricBar({ value, max, color = 'bg-cyan-500' }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="w-full h-2 bg-slate-700/50 rounded-full overflow-hidden">
      <div className={`h-full ${color} rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, color = 'text-cyan-400' }) {
  return (
    <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} className={color} />
        <span className="text-xs text-slate-400 uppercase tracking-wide">{label}</span>
      </div>
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function EndpointRow({ ep, globalMax }) {
  const avgColor = ep.avg_ms > 3000 ? 'text-red-400' : ep.avg_ms > 1000 ? 'text-amber-400' : 'text-emerald-400';
  const barColor = ep.avg_ms > 3000 ? 'bg-red-500' : ep.avg_ms > 1000 ? 'bg-amber-500' : 'bg-emerald-500';

  return (
    <div className="group flex items-center gap-3 py-2.5 px-3 hover:bg-slate-700/30 rounded-lg transition-colors" data-testid={`endpoint-row-${ep.endpoint.replace(/\s/g, '-')}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-700 text-slate-300">
            {ep.endpoint.split(' ')[0]}
          </span>
          <span className="text-sm font-mono text-slate-200 truncate">{ep.endpoint.split(' ').slice(1).join(' ')}</span>
        </div>
        <div className="mt-1.5">
          <MetricBar value={ep.avg_ms} max={globalMax} color={barColor} />
        </div>
      </div>
      <div className="flex items-center gap-4 text-right shrink-0">
        <div>
          <div className={`text-sm font-semibold tabular-nums ${avgColor}`}>{ep.avg_ms.toLocaleString()}ms</div>
          <div className="text-[10px] text-slate-500">avg</div>
        </div>
        <div>
          <div className="text-sm font-semibold tabular-nums text-slate-300">{ep.max_ms.toLocaleString()}ms</div>
          <div className="text-[10px] text-slate-500">max</div>
        </div>
        <div>
          <div className="text-sm font-semibold tabular-nums text-slate-300">{ep.requests}</div>
          <div className="text-[10px] text-slate-500">reqs</div>
        </div>
        {ep.errors > 0 && (
          <div>
            <div className="text-sm font-semibold tabular-nums text-red-400">{ep.error_rate_pct}%</div>
            <div className="text-[10px] text-slate-500">err</div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ApiMetrics() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [sortBy, setSortBy] = useState('avg_ms');

  const fetchMetrics = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/metrics`, { credentials: 'include' });
      if (res.ok) setMetrics(await res.json());
      else setMetrics(null);
    } catch (e) {
      console.error('Failed to fetch metrics:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMetrics();
    if (!autoRefresh) return;
    const id = setInterval(fetchMetrics, 10000);
    return () => clearInterval(id);
  }, [fetchMetrics, autoRefresh]);

  const handleReset = async () => {
    if (!window.confirm('Reset all collected metrics?')) return;
    await fetch(`${API_URL}/api/metrics/reset`, { method: 'DELETE', credentials: 'include' });
    fetchMetrics();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw size={20} className="animate-spin text-cyan-400 mr-3" />
        <span className="text-slate-400">Loading metrics...</span>
      </div>
    );
  }

  if (!metrics) {
    return <div className="text-center py-20 text-slate-500" data-testid="metrics-unavailable">Could not load metrics (administrator access required).</div>;
  }

  const { summary, slowest_endpoints, busiest_endpoints, error_prone_endpoints, all_endpoints } = metrics;
  const sorted = [...all_endpoints].sort((a, b) => {
    if (sortBy === 'avg_ms') return b.avg_ms - a.avg_ms;
    if (sortBy === 'requests') return b.requests - a.requests;
    if (sortBy === 'errors') return b.error_rate_pct - a.error_rate_pct;
    return b.max_ms - a.max_ms;
  });
  const globalMax = Math.max(...all_endpoints.map(e => e.avg_ms), 1);

  return (
    <div className="space-y-6" data-testid="api-metrics-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Activity size={20} className="text-cyan-400" /> API Performance Monitor
          </h2>
          <p className="text-xs text-slate-500 mt-1">Since {new Date(summary.uptime_since).toLocaleString()}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${autoRefresh ? 'border-cyan-500/50 text-cyan-400 bg-cyan-500/10' : 'border-slate-600 text-slate-400 bg-slate-800'}`}
            data-testid="auto-refresh-toggle"
          >
            {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
          </button>
          <button onClick={fetchMetrics} className="p-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-400 hover:text-white transition-colors" data-testid="refresh-metrics-btn">
            <RefreshCw size={14} />
          </button>
          <button onClick={handleReset} className="p-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-400 hover:text-red-400 transition-colors" data-testid="reset-metrics-btn">
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Zap} label="Total Requests" value={summary.total_requests.toLocaleString()} />
        <StatCard icon={AlertTriangle} label="Errors" value={summary.total_errors} sub={`${summary.error_rate_pct}% error rate`} color="text-red-400" />
        <StatCard icon={BarChart3} label="Endpoints Tracked" value={summary.tracked_endpoints} color="text-indigo-400" />
        <StatCard icon={Clock} label="Slow Threshold" value={`${(summary.slow_threshold_ms / 1000).toFixed(0)}s`} sub="requests flagged above" color="text-amber-400" />
      </div>

      {/* Top 5 Panels */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Slowest */}
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-amber-400 flex items-center gap-1.5 mb-3">
            <Clock size={14} /> Slowest (avg)
          </h3>
          <div className="space-y-2">
            {slowest_endpoints.map((ep, i) => (
              <div key={i} className="flex justify-between items-center text-xs">
                <span className="text-slate-300 truncate max-w-[60%] font-mono">{ep.endpoint.split(' ').pop()}</span>
                <span className="text-amber-400 font-semibold tabular-nums">{ep.avg_ms.toLocaleString()}ms</span>
              </div>
            ))}
            {slowest_endpoints.length === 0 && <div className="text-xs text-slate-500">No data yet</div>}
          </div>
        </div>
        {/* Busiest */}
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-cyan-400 flex items-center gap-1.5 mb-3">
            <TrendingUp size={14} /> Most Called
          </h3>
          <div className="space-y-2">
            {busiest_endpoints.map((ep, i) => (
              <div key={i} className="flex justify-between items-center text-xs">
                <span className="text-slate-300 truncate max-w-[60%] font-mono">{ep.endpoint.split(' ').pop()}</span>
                <span className="text-cyan-400 font-semibold tabular-nums">{ep.requests} reqs</span>
              </div>
            ))}
            {busiest_endpoints.length === 0 && <div className="text-xs text-slate-500">No data yet</div>}
          </div>
        </div>
        {/* Error Prone */}
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-red-400 flex items-center gap-1.5 mb-3">
            <AlertTriangle size={14} /> Error Prone
          </h3>
          <div className="space-y-2">
            {error_prone_endpoints.map((ep, i) => (
              <div key={i} className="flex justify-between items-center text-xs">
                <span className="text-slate-300 truncate max-w-[60%] font-mono">{ep.endpoint.split(' ').pop()}</span>
                <span className="text-red-400 font-semibold tabular-nums">{ep.error_rate_pct}%</span>
              </div>
            ))}
            {error_prone_endpoints.length === 0 && <div className="text-xs text-slate-500 italic">No errors detected</div>}
          </div>
        </div>
      </div>

      {/* All Endpoints Table */}
      <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700/50">
          <h3 className="text-sm font-semibold text-white">All Endpoints</h3>
          <div className="flex items-center gap-1">
            {['avg_ms', 'max_ms', 'requests', 'errors'].map(key => (
              <button
                key={key}
                onClick={() => setSortBy(key)}
                className={`text-[10px] px-2 py-1 rounded transition-colors ${sortBy === key ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' : 'text-slate-500 hover:text-slate-300'}`}
              >
                {key === 'avg_ms' ? 'Avg' : key === 'max_ms' ? 'Max' : key === 'requests' ? 'Calls' : 'Errors'}
              </button>
            ))}
          </div>
        </div>
        <div className="max-h-[400px] overflow-y-auto divide-y divide-slate-700/30">
          {sorted.map((ep, i) => <EndpointRow key={i} ep={ep} globalMax={globalMax} />)}
          {sorted.length === 0 && (
            <div className="text-center py-8 text-slate-500 text-sm">No API calls recorded yet. Use the app to generate data.</div>
          )}
        </div>
      </div>
    </div>
  );
}
